import { Component, OnInit, OnDestroy, HostListener, ViewEncapsulation } from "@angular/core";
import { BehaviorSubject, Subscription, Observable, EMPTY, Subject, of } from "rxjs";
import { GuideDTO, PlaylistDaySchedule } from "src/app/models/guides/guideDTO";
import { TopCard } from "src/app/models/top-card/top-card.model";

import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { cloneDeep } from "lodash";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { MatDialog } from "@angular/material/dialog";
import {
  DefaultConfirmationDialogData,
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { GuideService } from "src/app/shared/services/guides/guide.service";
import { catchError, finalize, tap, map, filter, take, switchMap, takeUntil } from "rxjs/operators";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { Title } from "@angular/platform-browser";
import { SelectPlaylistDialogComponent } from "src/app/shared/modules/dialogs/select-playlist-dialog/select-playlist-dialog.component";
import { CdkDragDrop, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { RolePermissionsService } from '../../../shared/services/role-permissions/role-permissions.service';

export const FORM_PARAMS = {
  name: "name",
};

export enum DayOfWeekEnum {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}
export interface PlaylistView {
  playlist: { token: string; name: string, terminalType?: string };
  timeStart: string;
  timeEnd: string;
  dayWeek: string;
}
interface ScheduleToBackend {
  [day: string]: PlaylistDaySchedule[];
}

@Component({
  selector: "app-guide-detail",
  templateUrl: "./guide-detail.component.html",
  styleUrls: ["./guide-detail.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class GuideDetailComponent implements OnInit, OnDestroy {
  daysOfWeek = [
    DayOfWeekEnum.MONDAY,
    DayOfWeekEnum.TUESDAY,
    DayOfWeekEnum.WEDNESDAY,
    DayOfWeekEnum.THURSDAY,
    DayOfWeekEnum.FRIDAY,
    DayOfWeekEnum.SATURDAY,
    DayOfWeekEnum.SUNDAY,
  ];

  hours = Array.from({ length: 24 }, (_, i) => i);
  schedule: { [ day: string]: { [hour:number]: PlaylistView[] } } = {};

  isResizing = false;

  isLoading$ = new BehaviorSubject<boolean>(false);
  guideData: Subscription;
  created$: Observable<boolean>;
  public terminalType$ = new BehaviorSubject<string>(null);

  //Permissions
  public canWrite$: Observable<boolean> = of(true);
  protected destroy$ = new Subject<void>();
  canWrite: boolean = false;

  card: TopCard;
  icon = "add";

  isDragging = false;

  currCode: string;
  currentGuide: GuideDTO;

  creation: boolean = false;
  duplication: boolean = false;

  guideDataForm: FormGroup;

  langSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private guidesService: GuideService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private titleService: Title,
    private fb: FormBuilder,
    private rolePermissionsService: RolePermissionsService
  ) {
    this.titleService.setTitle(this.translate.instant("Guide.details"));

    this.setTopcard();
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";

    this.route.params.subscribe((params) => {
      const code = params.id;
      this.creation = false;
      this.duplication = false;
      const duplicateCode = localStorage.getItem("duplicate");

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      if (code && code?.length) {
        this.initialiseState(code, false);
      } else if (duplicateCode !== null) {
        this.duplication = true;
        localStorage.removeItem("duplicate");
        this.initialiseState(duplicateCode, true);
      } else {
        this.creation = true;
        this.initialiseState("", false);
      }
    });

    this.loadLangChanges();
    this.createGuideForm();
    this.initialiseSchedule();
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(()=> 
          this.rolePermissionsService.hasPermission(
            "MULTIMEDIA_GUIDE_MANAGEMENT",
            "WRITE"
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if(!canEdit) {
          this.disableFormControls(this.guideDataForm);
        }
      });

      this.canWrite$ = this.rolePermissionsService.hasPermission(
        "MULTIMEDIA_GUIDE_MANAGEMENT",
        "READ"
      );
  }

  protected disableFormControls(form: FormGroup): void {
    const fields = [
      FORM_PARAMS.name
    ];

    fields.forEach((field) => form.get(field)?.disable());
  }

  initialiseSchedule() {
    if(this.schedule && Object.keys(this.schedule).length > 0) return;

    this.schedule = {};

    this.daysOfWeek.forEach((day) => {
      this.schedule[day] = {};
      this.hours.forEach(hour=>{
        this.schedule[day][hour] = []
      })

    });
  }

  createGuideForm() {
    this.guideDataForm = this.fb.group({
      [FORM_PARAMS.name]: [
        null,
        [Validators.required, Validators.maxLength(100)],
      ],
    });

    this.checkPermissions();
  }

  setGuideName(guide) {
    this.guidesService.getGuide(guide.token).subscribe((response) => {
      guide.name = response.name;
    });
  }

  initialiseState(code: string, duplicate: boolean) {
    if (code) {
      this.getGuide(code);
      if (duplicate) {
        this.currCode = "";
      } else {
        this.currCode = code;
      }
    } else {
      this.currCode = "";
      this.setTopcard();
    }
  }

  setTopcard(guide?: GuideDTO) {
    if (guide) {
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: !this.duplication ? guide?.name : "",
        title: !this.duplication ? guide?.name ?? "" : "Guide.newGuide",
        subtitle: !this.duplication ? "Guide.guide" : "Guide.duplication",
      };
    } else {
      this.card = {
        avatarUrl: "",
        content: "",
        imageUrl: "",
        current: "",
        title: "Guide.newGuide",
        subtitle: this.duplication ? "Guide.duplication" : "Guide.creation",
      };
    }
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(this.translate.instant("Guide.guides"));
    });
  }

  hasAnyItemsAtHour(hour: number):boolean {
    return this.daysOfWeek.some(day =>
      this.getScheduleItems(day, hour).length > 0
    );
  }

  getScheduleItems(day: string, hour: number): PlaylistView[] {
    return this.schedule[day]?.[hour] || [];
  }

  getDropListId(day: string, hour: number): string{
    return `${day}-${hour}`;
  }

  getHourString(hour: number): string {
    return hour.toString().padStart(2, '0') + ":00:00"
  }

  getArrayHours(timeStart: string, timeEnd: string): number[]{
    const hourStart = this.getFormatHourToNumber(timeStart)
    const hourEnd = this.getFormatHourToNumber(timeEnd);

    const hours: number[]= [];
    
    let i = hourStart;
    do {
      hours.push(i);
      i++;
    } while (i < hourEnd);
    
    return hours;
  }

  getFormatHourToNumber(hour: string):number{
    const parse = hour.split(':');
    return parseInt(parse[0], 10)
  }

  getGuide(code: string) {
    if (this.creation) return;

    this.isLoading$.next(true);
    this.guidesService
      .getGuide(code)
      .pipe(
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Guide.guideLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((response) => {
        const treatedSequenceDTO = {
          ...response,
          schedules: response.schedules,

        };
        this.currentGuide = cloneDeep(treatedSequenceDTO);
        this.terminalType$.next(this.currentGuide?.inferredTerminalType)
        const treatedPlaylists: PlaylistView[] = this.scheduleToPlaylistView(response.schedules);
  
        treatedPlaylists.forEach(p => this.addToSchedule(p));

        if (!this.duplication) {
          this.guideDataForm.controls.name.setValue(response.name);
        }

        if (
          (!this.currCode || (this.currCode && this.currCode?.length === 0)) &&
          !this.duplication
        ) {
          this.currCode = this.currentGuide.token;
        }

        this.setTopcard(response);
      });
  }

  createGuide(guide: any) {
    this.isLoading$.next(true);
    this.guidesService
      .createGuide(guide)
      .pipe(
        tap((response) => {
          this.snackbarService.show({
            message: this.translate.instant("Guide.guideCreated"),
            type: SnackbarType.SUCCESS,
          });

          this.creation = false;
          this.duplication = false;

          this.currentGuide = cloneDeep(response);
          this.currCode = this.currentGuide.token;

          this.setTopcard(response);

          this.guideDataForm.markAsPristine();
          this.guideDataForm.markAsUntouched();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  updateGuide(guide: any) {
    this.isLoading$.next(true);
    this.guidesService
      .updateGuide(guide, this.currCode)
      .pipe(
        tap((updatedGuide) => {
          this.snackbarService.show({
            message: this.translate.instant("Guide.guideUpdated"),
            type: SnackbarType.SUCCESS,
          });

          this.currentGuide = cloneDeep(updatedGuide);
          this.setTopcard(updatedGuide);

          this.guideDataForm.markAsPristine();
          this.guideDataForm.markAsUntouched();
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  deleteGuide() {
    this.isLoading$.next(true);
    this.guidesService
      .deleteGuide(this.currCode)
      .pipe(
        tap(() => {
          this.router.navigate(["/multimedia-guides", {}]);
          this.snackbarService.show({
            message: this.translate.instant("Guide.guideDeleted"),
            type: SnackbarType.SUCCESS,
          });
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant(error),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  scheduleToPlaylistView(schedule: { [day: string]: PlaylistDaySchedule[] }): PlaylistView[] {
    const playlistViews: PlaylistView[] = [];
    const type = this.currentGuide?.inferredTerminalType;

    Object.keys(schedule).forEach(dayWeek => {
      schedule[dayWeek].forEach(item => {
        playlistViews.push({
          playlist: {
            token: item.playlist.token,
            name: item.playlist.name,
            terminalType: type
          },
          timeStart: item.timeStart,
          timeEnd: item.timeEnd,
          dayWeek: dayWeek
        });
      });
    });
  
    return playlistViews;
  }

  viewToScheduleBackend(playlistViews: {[day:string]:{[hour:number]: PlaylistView[]}}): ScheduleToBackend{
    const schedule: ScheduleToBackend = {}

    Object.keys(playlistViews).forEach(day => {
      Object.keys(playlistViews[day]).forEach(hour => {
        playlistViews[day][Number(hour)].forEach(view => {
          if(!schedule[day]) {
            schedule[day] = []
          }

          const startHour = parseInt(view.timeStart.split(':')[0]);
          let endHour = parseInt(view.timeEnd.split(':')[0])

          const normalizedStartTime = `${String(startHour).padStart(2, '0')}:00:00`;
          const normalizedEndTime = `${String(endHour).padStart(2,'0')}:59:59`;

          schedule[day].push({
            timeStart: normalizedStartTime,
            timeEnd: normalizedEndTime,
            playlist: {
              name: view.playlist.name,
              token: view.playlist.token
            }
          });
        });
      })
    })

    return schedule
  }

  addToSchedule(playlist: PlaylistView) {     
    const arrayHours: number[] = this.getArrayHours(playlist.timeStart, playlist.timeEnd);

    arrayHours.forEach(hour => {
      if (!this.schedule[playlist.dayWeek]) {
        this.schedule[playlist.dayWeek] = {};
      }
      
      if (!this.schedule[playlist.dayWeek][hour]) {
        this.schedule[playlist.dayWeek][hour] = [];
      }
      
      this.schedule[playlist.dayWeek][hour].push({
        ...playlist, 
        timeStart: `${String(hour).padStart(2, '0')}:00:00`, 
        timeEnd:`${String(hour).padStart(2, '0')}:59:59`
      });
    });

    this.schedule = { ...this.schedule };
  }

  openSelectPlaylistDialog(
    day?: string,
    hour?: number
  ) { 
    const dialogData = {
      width: "1200px",
      height: "85vh",
      panelClass: "select-playlist-dialog",
      autoFocus: false,
      data: {
        preDay: day,
        preTimeStart: hour,
        preTimeEnd: hour + 1,
        terminalType: this.terminalType$.value,
        schedule: this.schedule
      }
    };

    const dialogRef = this.dialog.open(
      SelectPlaylistDialogComponent,
      dialogData
    );

    dialogRef.afterClosed().subscribe((result) => {
      if(!result) return;

      const playlists = Array.isArray(result) ? result : [result];

      playlists.forEach((selectedPlaylist) => {
        const newScheduleItem: PlaylistView = {
          playlist: { 
            token: selectedPlaylist.token, 
            name: selectedPlaylist.name 
          },
          timeStart: this.getHourString(selectedPlaylist.timeStart),
          timeEnd: this.getHourString(selectedPlaylist.timeEnd),
          dayWeek: selectedPlaylist.dayWeek 
        };
        this.addToSchedule(newScheduleItem);
      });

      this.terminalType$.next(playlists[0].terminalType);
    });
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  refresh() {
    if (isAuthenticated(this.router.url) && !this.guideDataForm.pristine) {
      this.confirmDialog(this.dialog, true);
      return;
    } else {
      this.reload();
    }
  }

  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["multimedia-guides/guide", this.currCode]);
  }

  createNew() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["multimedia-guides/guide"]);
    this.creation = true;
  }

  duplicate() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    localStorage.setItem("duplicate", this.currCode);
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["multimedia-guides/guide", {}]);
  }

  onSubmit() {
    if (!this.guideDataForm.valid) {
      this.guideDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("Guide.guideFormInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (
      this.guideDataForm.pristine &&
      !this.creation &&
      !this.duplication &&
      this.noChangesGuide()
    ) {
      this.snackbarService.show({
        message: this.translate.instant("Guide.noChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }
    
    const newSchedule = this.viewToScheduleBackend(this.schedule)

    let guide = {
      name: this.guideDataForm.controls[FORM_PARAMS.name].value ?? null,
      schedules: newSchedule,
    };

    if (!this.currCode) {
      this.createGuide(guide);
    } else {
      this.updateGuide(guide);
    }
  }

  noChangesGuide(): boolean {
    if (!this.currentGuide) {
      return true;
    }

    const newSchedule = this.viewToScheduleBackend(this.schedule);
    
    const currentNormalized = this.normalizedScheduleGuide(this.currentGuide.schedules);
    const newNormalized = this.normalizedScheduleGuide(newSchedule);

    return currentNormalized === newNormalized;
  }

  normalizedScheduleGuide(schedule: ScheduleToBackend): string {
    const normalized: ScheduleToBackend = {}

    Object.keys(schedule).sort().forEach(day => {
      normalized[day] = schedule[day].map(item => ({
        timeStart: item.timeStart,
        timeEnd: item.timeEnd,
        playlist: {
          name: item.playlist.name,
          token: item.playlist.token
        }
      }));
    });

    return JSON.stringify(normalized);
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (
      !isAuthenticated(this.router.url) ||
      (this.guideDataForm.pristine && this.noChangesGuide())
    ) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any, refresh = false): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    if (!refresh) {
      return dialogRef.afterClosed();
    }
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.reload();
      }
    });
  }

  openGuideDeleteDialog() {
    const dialogData: Dialog = DefaultConfirmationDialogData;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, dialogData);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteGuide();
      }
    });
  }

  onDragStarted() {
    this.isDragging = true;
  }

  onDragEnded() {
    this.isDragging = false
  }

  onDrop(
    event: CdkDragDrop<PlaylistView[]>, 
    targetDay: string,
    targetHour: number
  ){
  
    const sourceData = event.previousContainer.data;
    const targetData = event.container.data;

    if(targetData.length){
      this.snackbarService.show({
        message: this.translate.instant("Guide.slotOccupied"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if(event.previousContainer == event.container){
      moveItemInArray(sourceData, event.previousIndex, event.currentIndex);
    }else{
      const item = sourceData[event.previousIndex]

      const originalStartHour = parseInt(item.timeStart.split(':')[0], 10);
      const originalEndHour = parseInt(item.timeEnd.split(':')[0], 10);
      const duration = originalEndHour - originalStartHour;

      const newItem: PlaylistView = {
        ...item,
        timeStart: targetHour.toString().padStart(2, '0') + ':00',
        timeEnd: Math.min(targetHour + duration, 24).toString().padStart(2, '0') + ':00'
      };

      transferArrayItem(
        sourceData,
        targetData,
        event.previousIndex,
        event.currentIndex
      );

      targetData[event.currentIndex] = newItem;
    }
  }

  removePlaylistFromSchedule(day: string, hour: number, token: string) {
    const item = this.schedule[day][hour].find(
      (i) => i.playlist.token === token
    );
    
    this.schedule[day][hour] = this.schedule[day][hour].filter(
      (item) => item.playlist.token !== token
    );

    this.schedule = { ...this.schedule };

    if (item) {
      this.snackbarService.show({
        message: this.translate.instant("Guide.removePlaylist"),
        type: SnackbarType.SUCCESS,
      });
    }

    const hasAnyPlaylist = Object.values(this.schedule).some(day =>
      Object.values(day).some(hourArr => hourArr.length > 0)
    );

    if(!hasAnyPlaylist){
      this.terminalType$.next(null)
    }
  }

  sortItemsByTime(items: PlaylistDaySchedule[]): PlaylistDaySchedule[] {
    return [...items].sort((a, b) => {
      return a.timeStart.localeCompare(b.timeStart);
    });
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
